import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Button, Modal, Switch, TextInput } from '@mantine/core'

import { GET_ADMIN_COMPANIES } from 'src/graphql/apiClientAccesses.graphql'

import CompanySelect from './CompanySelect'
import ScopesInput from './ScopesInput'

export type ApiClientAccessFormValues = {
  clientId: string
  companyId: string
  scopes: string[]
  active: boolean
}

export type ApiClientAccessFormRow = {
  apiClientAccessId: string
  clientId: string
  clientName: string | null
  clientStatus: 'KNOWN' | 'UNKNOWN' | 'UNVERIFIED'
  company: { companyId: string; companyName: string }
  scopes: string[]
  active: boolean
}

type ApiClientAccessFormModalProps = {
  opened: boolean
  onClose: () => void
  mode: 'create' | 'edit'
  row?: ApiClientAccessFormRow
  onSubmit: (values: ApiClientAccessFormValues) => void
  submitting: boolean
}

const ApiClientAccessFormModal = ({
  opened,
  onClose,
  mode,
  row,
  onSubmit,
  submitting,
}: ApiClientAccessFormModalProps) => {
  const [clientId, setClientId] = useState('')
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [scopes, setScopes] = useState<string[]>([])
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (!opened) return
    setClientId(row?.clientId ?? '')
    setCompanyId(row?.company.companyId ?? null)
    setScopes(row?.scopes ?? [])
    setActive(row?.active ?? true)
  }, [opened, row])

  const { data: companiesData } = useQuery(GET_ADMIN_COMPANIES, {
    variables: { search: null },
    skip: mode === 'edit',
  })

  const companies = companiesData?.adminCompanies ?? []

  const canSubmit =
    clientId.trim().length > 0 && !!companyId && scopes.length > 0

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === 'create' ? 'Add an API client' : 'Edit access'}
      centered
    >
      <div className="flex flex-col gap-4 text-sm">
        {mode === 'create' ? (
          <TextInput
            id="api-client-form-client-id"
            label="Client id"
            description="The Auth0 application's client id. The name shows in the list once saved."
            placeholder="abc123DEF456"
            value={clientId}
            onChange={(event) => setClientId(event.currentTarget.value)}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-doubleNickel-gray-600">
              Client
            </span>
            <span className="text-doubleNickel-gray-900">
              {row?.clientStatus === 'KNOWN'
                ? row.clientName || row.clientId
                : row?.clientStatus === 'UNVERIFIED'
                ? 'Unverified client'
                : 'Unknown client'}
            </span>
            <span className="font-mono text-xs text-doubleNickel-gray-500">
              {row?.clientId}
            </span>
          </div>
        )}
        {mode === 'create' ? (
          <CompanySelect
            id="api-client-form-company"
            label="Company"
            placeholder="Search by name or id"
            companies={companies}
            value={companyId}
            onChange={setCompanyId}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-doubleNickel-gray-600">
              Company
            </span>
            <span className="text-doubleNickel-gray-900">
              {row?.company.companyName}
            </span>
            <span className="font-mono text-xs text-doubleNickel-gray-500">
              {row?.company.companyId}
            </span>
          </div>
        )}
        <ScopesInput
          id="api-client-form-scopes"
          label="Scopes"
          placeholder="Pick or type a scope"
          value={scopes}
          onChange={setScopes}
        />
        {mode === 'edit' && (
          <Switch
            size="xs"
            label="Active"
            description="Inactive rows are still allowed until enforcement is turned on."
            checked={active}
            onChange={(event) => setActive(event.currentTarget.checked)}
          />
        )}
        {mode === 'edit' && (
          <div className="text-xs text-doubleNickel-gray-600">
            Scopes the client keeps using are added back automatically. Blocking
            is not enabled yet.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submitting}
            disabled={!canSubmit}
            onClick={() =>
              companyId &&
              onSubmit({ clientId: clientId.trim(), companyId, scopes, active })
            }
          >
            {mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ApiClientAccessFormModal
